// TypeScript shapes mirroring the backend DTOs in
// src/PhoenixCapital.Core/DTOs/DealFlow/DealFlowDtos.cs (field-by-field).
//
// All enums are persisted on the wire as their string member name (the
// JsonStringEnumConverter is wired by Program.cs), matching the convention
// used by the existing Deals / Brokers modules.

// ============================== enums =================================

/** Status of a single CriteriaChecklistItem. */
export type CriteriaItemStatus =
  | "Pending"
  | "Escalated"
  | "Approved"
  | "Declined"
  | "Waived";

/** DQR tier classification. */
export type CriteriaTier = "DealKiller" | "RedFlag" | "NeedsImprovement";

/** Analyst lane. */
export type CriteriaLane = "A1" | "A2" | "A3" | "A4";

/** Pass/fail outcome on a lane assessment. */
export type LaneOutcome = "Pass" | "Fail";

/** DQR (1=Excellent, 5=Poor). */
export type DealQualityRating =
  | "Excellent"
  | "Good"
  | "Satisfactory"
  | "Fair"
  | "Poor";

/** 7-pill assignment slot status. */
export type DealAssignmentStatus = "Unassigned" | "Assigned" | "NotApplicable";

/** DealFailure lifecycle. */
export type FailureStatus = "Open" | "Reviewed" | "Resolved";

/** Final Financial Addendum letter type. */
export type LetterType = "Approval" | "Rejection";

/** Deal Flow detail-page tab identifier (used by the
 *  certification ceremony payload to keep cert rows organized). */
export type DealFlowTab =
  | "Underwriting"
  | "FinancialAnalysis"
  | "LegalAnalysis"
  | "BusinessAnalysis"
  | "Closing";

/** The 7 assignable roles on a Deal. The string is the role
 *  taxonomy code; backend persists this verbatim on DealAssignment.RoleCode. */
export type DealRoleCode = "C1" | "B1" | "A1" | "A2" | "A3" | "A4" | "B2";

export const DEAL_ROLE_CODES: readonly DealRoleCode[] = [
  "C1",
  "B1",
  "A1",
  "A2",
  "A3",
  "A4",
  "B2",
] as const;

/** Human-readable role labels. */
export const DEAL_ROLE_LABEL: Record<DealRoleCode, string> = {
  C1: "Broker Liaison",
  B1: "Sr Loan Admin",
  A1: "Underwriter",
  A2: "Financial Analyst",
  A3: "Legal Analyst",
  A4: "Market & Credit Research",
  B2: "Closing Coordinator",
};

// ============================== catalog ===============================

export interface CriteriaCatalogItemDto {
  id: string;
  code: string;
  title: string;
  tier: CriteriaTier;
  lane: CriteriaLane;
  description: string;
  requiredAction: string;
  escalationPath: string | null;
  resolutionGuidance: string | null;
  isCommonExpected: boolean;
  dependsOnItemId: string | null;
  /** JSON-encoded FormSchema. Empty/blank → render the generic textarea
   *  fallback in <CriteriaChecklist>. */
  formSchema: string;
  blaSectionRefs: string | null;
  dqrPolicyVersion: string;
  isActive: boolean;
}

/** Admin edit of a catalog template (PATCH /deal-flow/catalog/{id}). Only the
 *  six author-editable copy fields; edits propagate to every deal because
 *  per-deal instances FK to the catalog by GUID with no text snapshot. */
export interface UpdateCatalogItemRequest {
  title: string;
  description: string;
  requiredAction: string;
  escalationPath: string | null;
  isCommonExpected: boolean;
  resolutionGuidance: string | null;
}

/** ADMIN-only add of a catalog item (POST /deal-flow/catalog). `lane` is fixed
 *  by the analyst page; `tier` is chosen in the form. The `code` is generated
 *  server-side from lane + tier — it is never sent by the client. */
export interface CreateCatalogItemRequest {
  lane: CriteriaLane;
  tier: CriteriaTier;
  title: string;
  description: string;
  requiredAction: string;
  escalationPath: string | null;
  isCommonExpected: boolean;
  resolutionGuidance: string | null;
}

// ============================== checklist =============================

/** Analyst's severity judgment on a criteria checklist item. */
export type CriteriaItemAssessment = "Clean" | "Minor" | "Material" | "Critical";

export const CRITERIA_ITEM_ASSESSMENTS: readonly CriteriaItemAssessment[] = [
  "Clean",
  "Minor",
  "Material",
  "Critical",
] as const;

export interface CriteriaChecklistItemDto {
  id: string;
  dealId: string;
  catalogItemId: string;
  code: string;
  title: string;
  lane: CriteriaLane;
  tier: CriteriaTier;
  status: CriteriaItemStatus;
  /** JSON blob whose shape is dictated by CriteriaCatalogItem.formSchema.
   *  When schema is empty/blank the value is treated as a free-form note. */
  justification: string | null;
  certifiedByUserId: string | null;
  certifiedAt: string | null;
  failureReason: string | null;
  attachedDocumentId: string | null;
  escalatedToUserId: string | null;
  escalatedAt: string | null;
  waivedByUserId: string | null;
  waiverRationale: string | null;
  /** Analyst's severity judgment. null when not yet assessed. */
  assessment: CriteriaItemAssessment | null;
  /** Analyst's self-attestation that the associated report has been
   *  uploaded to the Document Repository. Gates Certify + Decline. */
  reportUploadedAttested: boolean;
  reportUploadedAttestedByUserId: string | null;
  reportUploadedAttestedAt: string | null;
  /** Attester identity resolved server-side in the read
   *  projection so the modal can render a human-readable stamp instead of
   *  leaking the raw FK GUID. Falls back to UserName → Email → "Unknown
   *  user" when DisplayName is empty. Null when not yet attested. */
  reportUploadedAttestedByDisplayName: string | null;
  reportUploadedAttestedByEmail: string | null;
  /** Most-recent EscalationRecord for this item, surfaced so
   *  the row can render an outcome chip without a second roundtrip. */
  activeEscalation: ActiveEscalationSummary | null;
}

/** Active escalation lifecycle summary surfaced on the
 *  checklist row. Resolved* fields are null while status === "Pending". */
export interface ActiveEscalationSummary {
  escalationId: string;
  status: "Pending" | "Approved" | "Denied";
  escalatedAt: string;
  escalatedByUserName: string;
  escalationReason: string;
  resolvedAt: string | null;
  resolvedByUserName: string | null;
  resolutionDirective: string | null;
  resolutionNotes: string | null;
}

export interface UpdateJustificationRequest {
  justification?: string | null;
  /** Null = no change. String must be one of the four enum members. */
  assessment?: CriteriaItemAssessment | null;
  /** Null = no change. true/false flips the attestation. */
  reportUploadedAttested?: boolean | null;
}

export interface DeclineChecklistItemRequest {
  failureReason: string;
}

export interface WaiveChecklistItemRequest {
  rationale: string;
}

/** The admin reset's justification: 5–1000 characters after trimming,
 *  recorded in the reset audit. */
export interface ResetChecklistItemRequest {
  reason: string;
}

// ============================== lane assessments ======================

export interface LaneAssessmentDto {
  id: string;
  dealId: string;
  lane: CriteriaLane;
  outcomePassFail: LaneOutcome;
  dealQualityRating: DealQualityRating;
  failureReason: string | null;
  assessedByUserId: string;
  assessedAt: string;
}

export interface IssueLaneAssessmentRequest {
  outcomePassFail: LaneOutcome;
  dealQualityRating: DealQualityRating;
  failureReason: string | null;
}

// ============================== deal outcome ==========================

export interface DealOutcomeDto {
  id: string | null;
  dealId: string;
  /** Null before finalize until every non-N/A lane is issued (owner approval, 2026-10-13). */
  combinedDealQualityRating: number | null;
  /** Null before finalize until every non-N/A lane is issued. */
  overallPass: boolean | null;
  finalizedAt: string | null;
  finalizedByUserId: string | null;
  declinedAt: string | null;
  declinedReasonSummary: string | null;
  isFinalized: boolean;
}

// ============================== failures ==============================

export interface DealFailureDto {
  id: string;
  dealId: string;
  checklistItemId: string | null;
  lane: CriteriaLane;
  analystUserId: string;
  failureReason: string;
  status: FailureStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  certifiedResolved: boolean;
  createdAt: string;
}

export interface FailureListQuery {
  status?: FailureStatus;
  lane?: CriteriaLane;
  analyst?: string;
  dealId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

/** Paged response from <c>GET /api/deal-flow/failures</c>. */
export interface PagedFailures {
  items: DealFailureDto[];
  page: number;
  pageSize: number;
  totalCount: number;
}

// ============================== Overview tab ===========================

// "Stopped": the deal is in a terminal stage (Closed, Declined, Withdrawn), so
// its SLA clock no longer counts.
export type SlaHealthState = "Locked" | "OnTrack" | "AtRisk" | "Breached" | "Stopped";
export type OverallOutcomeState = "Pending" | "Pass" | "Fail";
export type LaneOutcomeState = "Awaiting" | "Pass" | "Fail" | "NotApplicable";

export interface TabProgress {
  tabCode: DealFlowTab;
  tabDisplayName: string;
  sectionsComplete: number;
  sectionsTotal: number;
  isLocked: boolean;
  lockReason: string | null;
  /** An analyst tab whose lane pill is Not Applicable: nothing to complete. */
  isNotApplicable: boolean;
}

export interface CertificationGatesPayload {
  tabs: TabProgress[];
}

export interface TabSlaStatus {
  tabCode: DealFlowTab;
  tabDisplayName: string;
  currentDay: number | null;
  targetDays: number | null;
  state: SlaHealthState;
}

export interface SlaHealthPayload {
  tabs: TabSlaStatus[];
}

export interface LaneOutcomeRow {
  laneCode: CriteriaLane;
  laneDisplayName: string;
  state: LaneOutcomeState;
  dealQualityRating: number | null;
  dealQualityRatingLabel: string | null;
}

export interface FinalOutcomePayload {
  laneOutcomes: LaneOutcomeRow[];
  combinedDealQualityRating: number | null;
  combinedDealQualityRatingLabel: string;
  overallOutcome: OverallOutcomeState;
  note: string;
}

export interface DealFlowOverviewResponse {
  certificationGates: CertificationGatesPayload;
  slaHealth: SlaHealthPayload;
  finalOutcome: FinalOutcomePayload;
  /** The deal is at or past Underwriting, or a Declined / Withdrawn deal whose
   *  stage history shows it got there (owner ruling, 2026-10-26). */
  reachedUnderwriting: boolean;
  /** The same for Closing. */
  reachedClosing: boolean;
}

export interface UpdateFailureRequest {
  status?: FailureStatus | null;
  resolutionNotes?: string | null;
}

export interface ResolveFailureRequest {
  resolutionNotes: string;
  certifiedResolved?: boolean;
}

// ============================== escrow ================================

export interface EscrowSetupDto {
  id: string;
  dealId: string;
  escrowComLink: string;
  amountUsd: number;
  issuedToBuyerAt: string | null;
  issuedByUserId: string | null;
  fundingVerifiedAt: string | null;
  fundingVerifiedByUserId: string | null;
  fundingVerifiedAmount: number | null;
  releasedAt: string | null;
  releasedByUserId: string | null;
  releasedAmount: number | null;
  /** Closing-gate BYPASS — set (with issuance + funding-verified stamped) when
   *  an ADMIN force-completes the Escrow Setup gate for a TEST deal so QA can
   *  advance without a real, funded escrow close. Null on a normal escrow row.
   *  Drives the "Bypassed for testing — no verified funds" banner. */
  bypassedAt: string | null;
  bypassedByUserId: string | null;
  bypassReason: string | null;
}

export interface UpsertEscrowSetupRequest {
  escrowComLink: string;
  amountUsd: number;
}

export interface CertifyEscrowIssuedRequest {
  issuedAt: string;
}

export interface CertifyEscrowFundingVerifiedRequest {
  verifiedAt: string;
  verifiedAmount: number;
}

/** Which escrow model a deal uses (owner ruling 5, 2026-10-08). */
export type DealEscrowModel = "None" | "Legacy" | "EscrowCom";

/** The server's one answer to "which escrow model, funded, released?" —
 *  `GET /deals/{dealId}/escrow/status` (DealEscrowStatusResolver). The Closing
 *  tab's Step 3 reads it rather than re-deriving it. */
export interface DealEscrowStatusDto {
  dealId: string;
  model: DealEscrowModel;
  escrowTransactionId: string | null;
  /** Underwriting Section 2: legacy issued + funding verified; Escrow.com buyer funded. */
  setupComplete: boolean;
  /** What release requires: legacy funding verified; Escrow.com buyer funded. */
  fundingVerified: boolean;
  /** Legacy only — the earliest day a release may carry. */
  fundingVerifiedAt: string | null;
  released: boolean;
  releasedAt: string | null;
  releasedAmount: number | null;
  /** Why the escrow-gate bypass API refuses this deal, in the server's words, or
   *  null when it accepts it (owner ruling, 2026-10-26: real escrow activity,
   *  pending or succeeded, on either model). Filled by GET …/escrow/status. */
  bypassBlockedReason?: string | null;
}

export interface ReleaseEscrowRequest {
  releasedAt: string;
  releasedAmount: number;
  transactionReference: string;
}

// ============================== ach info ==============================

/** The three Closing-tab ACH party slots. */
export type DealAchParty = "Borrower" | "Seller" | "Broker";

export const DEAL_ACH_PARTIES: readonly DealAchParty[] = [
  "Borrower",
  "Seller",
  "Broker",
] as const;

// ============================== payment setup (Closing Step 2) ==========
//
// The Closing-tab Step 2 "Payment Setup Status" surface. Read-only status
// over each party's linked portal account + its Stripe-sourced payment-method
// setup — the manual ACH bank-field capture / sign ceremony is retired. The
// three parties mirror the DealAchParty slots.

/** Closing Step 2 party slot. */
export type DealPaymentSetupParty = "Borrower" | "Seller" | "Broker";

/** Per-party payment-setup progress, derived server-side from the party's
 *  Stripe mandate / payout-account state. The three Track C sub-states are
 *  Borrower-only: `captured_preloan` (bank saved on the platform before any
 *  loan existed), `reauthorize_needed` (boarded onto a real servicer — the
 *  buyer must re-authorize on the servicer's account), `pending_servicer`
 *  (boarded but the servicer's Stripe account isn't ready — Phoenix's move,
 *  reminders suppressed). */
export type DealPaymentSetupStatus =
  | "not_linked"
  | "not_started"
  | "in_progress"
  | "captured_preloan"
  | "reauthorize_needed"
  | "servicer_changed"
  | "pending_servicer"
  | "complete";

/** How the party's portal account was resolved: inherited from the deal's
 *  counterparties, or a staff override applied on this Closing surface. */
export interface DealPaymentSetupLinkedAccountDto {
  portalUserId: string;
  fullName: string | null;
  email: string | null;
  source: "inherited" | "override";
}

export interface DealPaymentSetupPartyDto {
  party: DealPaymentSetupParty;
  linkedAccount: DealPaymentSetupLinkedAccountDto | null;
  setupStatus: DealPaymentSetupStatus;
  /** Stripe-sourced masked bank name — display-only, never a raw number. */
  bankName: string | null;
  /** Stripe-sourced last-4 of the bank account — display-only. */
  accountLast4: string | null;
  /** e.g. "ACH mandate" / "Payout account (ACH)". */
  methodLabel: string | null;
  completedAt: string | null;
  lastReminderSentAt: string | null;
  canRemind: boolean;
}

export interface DealPaymentSetupDto {
  /** Exactly three: Borrower, Seller, Broker. */
  parties: DealPaymentSetupPartyDto[];
}

// ============ servicing platform (TPS) — Closing Step 2 ================
//
// A staff-driven servicer decision recorded on the Closing tab ABOVE the party
// payment-setup rows: pick the certified servicer this loan will board onto, or
// explicitly keep the default placeholder for now. Distinct from the party
// self-service links — it is Phoenix's call, not the party's — and an explicit
// decision (real TPS or keep-default) is required before the loan can be
// created (Step-4 soft-gate).

/** The three servicer-decision states. */
export type DealTpsDecisionKind = "undecided" | "keep_default" | "selected";

export interface DealTpsAssignment {
  decision: DealTpsDecisionKind;
  tpsId: string | null;
  tpsName: string | null;
  decidedAt: string | null;
  decidedByUserName: string | null;
  /** True once the decision is frozen (deal Closed / loan created) — the block
   *  renders read-only. */
  locked: boolean;
}

/** A selectable servicer in the Step-2 picker. `isReady` is false when the firm
 *  is certified but its onboarding isn't complete — still selectable, surfaced
 *  with a muted hint. */
export interface DealTpsOption {
  tpsId: string;
  name: string;
  isReady: boolean;
}

/** PUT body — `tpsId` is required only when `decision === "selected"`. */
export interface SetDealTpsRequest {
  decision: DealTpsDecisionKind;
  tpsId?: string;
}

// ============================== loan creation =========================

export interface LoanCreationResultDto {
  loanId: string;
  loanNumber: string;
  createdAt: string;
  /** True when loan creation fell back to the default placeholder TPS because
   *  the recorded servicer decision could not be honored at boarding time
   *  (e.g. the selected TPS is no longer certified/ready). */
  tpsDriftedToDefault: boolean;
  /** Human-readable explanation of the drift, surfaced as a post-create toast.
   *  Null when no drift occurred. */
  tpsDriftMessage: string | null;
}

/** Create-Loan preview — resolved handoff terms + the suggested first-payment
 *  amount, so the confirm modal can prefill an editable amount before the loan
 *  exists.
 *
 *  WS-1b: `suggestedFirstPaymentAmount` is the COMBINED period-0 debit — the
 *  computed monthly P&I plus the BLA Core §2.4 Payment Administration Fee, at
 *  the standard rate boarding stamps on the loan — matching what the servicing ledger bills for period 0. The two
 *  components are broken out so the modal can show staff what they are
 *  collecting instead of one opaque total. Both are optional: an older payload
 *  omits them, and the total alone still prefills correctly. */
export interface CreateLoanPreviewDto {
  suggestedFirstPaymentAmount: number;
  loanAmount: number;
  interestRate: number;
  termMonths: number;
  amortizationMonths: number;
  firstPaymentDate: string;
  scheduledPandI?: number;
  paymentAdministrationFee?: number;
  /** The RATE the fee above was computed at, as a FRACTION (not a percent). On the
   *  wire so the modal can NAME the rate instead of printing a literal beside a
   *  computed dollar figure. Optional like its two siblings: an older payload
   *  omits it, and the sentence then names no rate at all rather than guessing
   *  one. */
  paymentAdministrationFeeRate?: number;
}

// ============================== assignments ===========================

export interface DealAssignmentDto {
  id: string;
  dealId: string;
  roleCode: string;
  status: DealAssignmentStatus;
  assignedUserId: string | null;
  assignedAt: string | null;
  assignedByUserId: string | null;
}

export interface UpsertAssignmentRequest {
  status: DealAssignmentStatus;
  assignedUserId: string | null;
}

// ============================== loan doc issuance =====================

export interface LoanDocumentIssuanceDto {
  id: string;
  dealId: string;
  // Buyer bundle (buyer portal)
  issuedAt: string | null;
  issuedByUserId: string | null;
  acknowledgedAt: string | null;
  signedDocumentsReceivedAt: string | null;
  // Seller bundle (seller portal; includes related exhibits)
  sellerIssuedAt: string | null;
  sellerAcknowledgedAt: string | null;
  sellerSignedDocumentsReceivedAt: string | null;
}

export interface UpsertLoanDocumentIssuanceRequest {
  issuedAt: string | null;
  acknowledgedAt: string | null;
  signedDocumentsReceivedAt: string | null;
  sellerIssuedAt: string | null;
  sellerAcknowledgedAt: string | null;
  sellerSignedDocumentsReceivedAt: string | null;
}

// ============================== final financial addendum ==============

export interface FinalFinancialAddendumDto {
  id: string;
  dealId: string;
  letterType: LetterType;
  letterIssuedAt: string | null;
  letterIssuedByUserId: string | null;
  loanAmount: number | null;
  interestRatePct: number | null;
  termMonths: number | null;
  covenantThresholdsJson: string | null;
  firstPaymentDateOverride: string | null;
}

export interface UpsertFinalFinancialAddendumRequest {
  letterType: LetterType;
  letterIssuedAt: string | null;
  loanAmount?: number | null;
  interestRatePct?: number | null;
  termMonths?: number | null;
  covenantThresholdsJson?: string | null;
  firstPaymentDateOverride?: string | null;
}

// ============================== certifications ========================

/** Unified certification lifecycle (analyst Section 2, Closing Step 1). A cert with
 *  no DB row, or a row whose status is "Pending", is treated as Pending. */
export type UnderwritingCertificationStatus =
  | "Pending"
  | "Certified"
  | "Declined"
  | "Escalated";

export interface UnderwritingCertificationDto {
  id: string;
  dealId: string;
  tab: DealFlowTab;
  section: number;
  certificationCode: string;
  label: string;
  /** Legacy SIGN ceremony fields — still populated for the Underwriting tab
   *  sections that keep the legacy POST /certifications endpoint. */
  signedByUserId: string | null;
  signedAt: string | null;
  justification: string | null;
  /** Lifecycle status mirroring the Section-1 checklist. */
  status: UnderwritingCertificationStatus;
  /** Analyst's severity judgment. null when not yet set. */
  assessment: CriteriaItemAssessment | null;
  reportUploadedAttested: boolean;
  reportUploadedAttestedAt: string | null;
  reportUploadedAttestedByUserId: string | null;
  reportUploadedAttestedByDisplayName: string | null;
  certifiedAt: string | null;
  certifiedByUserId: string | null;
  certifiedByDisplayName: string | null;
  declinedAt: string | null;
  declinedByUserId: string | null;
  declinedByDisplayName: string | null;
  declineReason: string | null;
  escalatedAt: string | null;
  escalatedByUserId: string | null;
  escalatedByDisplayName: string | null;
  escalationReason: string | null;
  /** The latest escalation record's status, or null when never escalated
   *  through one. An Escalated row may be certified or declined unless this is
   *  "Pending" (owner approval, 2026-10-13). */
  latestEscalationStatus?: "Pending" | "Approved" | "Denied" | null;
}

/** Legacy SIGN ceremony — Underwriting tab only. */
export interface SignCertificationRequest {
  tab: DealFlowTab;
  section: number;
  certificationCode: string;
  label: string;
  justification?: string | null;
}

/** Shared cert identity carried on every Section-2 request
 *  so the backend can upsert the UnderwritingCertification row. */
interface CertificationIdentityRequest {
  tab: DealFlowTab;
  section: number;
  certificationCode: string;
  label: string;
}

export interface CertifyCertificationRequest
  extends CertificationIdentityRequest {
  assessment: CriteriaItemAssessment | null;
  reportUploadedAttested: boolean;
  justification: string;
}

export interface DeclineCertificationRequest
  extends CertificationIdentityRequest {
  assessment: CriteriaItemAssessment | null;
  reportUploadedAttested: boolean;
  justification: string;
  declineReason: string;
}

export interface EscalateCertificationRequest
  extends CertificationIdentityRequest {
  assessment: CriteriaItemAssessment | null;
  escalationReason: string;
}

export interface SaveCertificationStateRequest
  extends CertificationIdentityRequest {
  assessment: CriteriaItemAssessment | null;
  reportUploadedAttested: boolean;
  justification: string | null;
}

// ============================== escalations ===========================

export type EscalationStatus = "Pending" | "Approved" | "Denied";

export interface EscalationRecordDto {
  id: string;
  checklistItemId: string;
  dealId: string;
  lane: CriteriaLane;
  escalatedByUserId: string;
  escalatedAt: string;
  escalationReason: string;
  status: EscalationStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  resolutionDirective: string | null;
}

export interface EscalationDetailDto {
  record: EscalationRecordDto;
  checklistItemCode: string | null;
  checklistItemTitle: string | null;
  checklistItemDescription: string | null;
  dealName: string | null;
}

export interface EscalationListItem {
  id: string;
  checklistItemId: string;
  dealId: string;
  lane: CriteriaLane;
  checklistItemCode: string | null;
  checklistItemTitle: string | null;
  dealName: string | null;
  escalatedByUserId: string;
  escalatedAt: string;
  escalationReason: string;
  status: EscalationStatus;
  resolvedByUserId: string | null;
  resolvedAt: string | null;
  resolutionDirective: string | null;
}

export interface EscalationListQuery {
  status?: EscalationStatus;
  lane?: CriteriaLane;
  analyst?: string;
  dealId?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
}

export interface PagedEscalations {
  items: EscalationListItem[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface EscalateChecklistItemRequest {
  escalationReason: string;
}

export interface ResolveEscalationRequest {
  resolutionNotes: string;
  resolutionDirective: string;
}

// ============================== Section 2 OPEN modal templates =========

export interface UnderwritingCertificationTemplateDto {
  certificationCode: string;
  tab: DealFlowTab;
  section: number;
  title: string;
  /** One-line blurb under the title on the Section 2 certification list. */
  shortDescription: string;
  description: string;
  requiredEvidence: string;
}

/** Admin edit of a certification template (PATCH /deal-flow/certification-
 *  templates/{code}). Only the four author-editable copy fields; edits
 *  propagate to every deal because per-deal instances reference the template
 *  by code with no text snapshot. */
export interface UpdateCertificationTemplateRequest {
  title: string;
  shortDescription: string;
  description: string;
  requiredEvidence: string;
}

/** ADMIN-only add of a Section 2 certification template (POST /deal-flow/
 *  certification-templates). `tab` is fixed by the analyst page; `key` is the
 *  bare PascalCase semantic name and the server derives the full code
 *  `<Fin|Leg|Bus>.S2.<key>`. */
export interface CreateCertificationTemplateRequest {
  tab: DealFlowTab;
  key: string;
  title: string;
  shortDescription: string;
  description: string;
  requiredEvidence: string;
}

// ============================== Admin Undo ==============================

export type UndoActionCode =
  | "Section9Handoff"
  | "S8CertSign"
  | "DealOutcomeFinalize"
  | "LaneAssessmentIssue"
  | "CriteriaItemTerminalAction"
  | "EscrowIssuance"
  | "EscrowFundingVerification"
  | "AnalystS2Certification"
  | "CriteriaItemReset"
  | "ClosingS1Certification";

export interface AdminUndoRequest {
  reason: string;
}

export interface AdminUndoResultDto {
  undoActionCode: UndoActionCode;
  dealId: string;
  targetEntityType: string;
  targetEntityId: string;
  occurredAt: string;
  adminUndoLogId: string;
}

export interface AdminUndoLogDto {
  id: string;
  occurredAt: string;
  actorUserId: string;
  actorDisplayName: string | null;
  dealId: string;
  undoActionCode: UndoActionCode;
  targetEntityType: string;
  targetEntityId: string;
  reason: string;
  forwardAuditEntryId: string | null;
  beforeStateJson: string | null;
  afterStateJson: string | null;
}

// ============================== Closing-gate Bypass =====================
//
// ADMIN-only break-glass to force-complete a closing gate for a TEST deal so
// QA can reach loan boarding without a real, funded escrow close. Only the
// Escrow Setup gate can be bypassed today.

/** The closing gates a bypass can target (EscrowSetup only today). */
export type DealGateCode = "EscrowSetup";

export interface DealGateBypassResultDto {
  gateCode: DealGateCode;
  dealId: string;
  targetEntityType: string;
  targetEntityId: string;
  occurredAt: string;
  bypassLogId: string;
}

export interface DealGateBypassLogDto {
  id: string;
  occurredAt: string;
  actorUserId: string;
  actorDisplayName: string | null;
  dealId: string;
  dealNumber: string | null;
  gateCode: DealGateCode;
  targetEntityType: string;
  targetEntityId: string;
  reason: string;
  afterStateJson: string | null;
}
